<template>
  <div
    :class="[
      'ra-star-rating',
      {
        'ra-star-rating--editable': !readonly,
        'ra-star-rating--force-focus-outline': forceFocusOutline,
      },
    ]"
  >
    <div
      v-if="readonly"
      role="img"
      :aria-label="getAriaLabel(roundedRating)"
      class="ra-star-rating__stars-wrapper"
    >
      <div v-for="n in maxRating" :key="n" class="ra-star-rating__star" :class="starClasses">
        <refapp-star-icon
          :width="starSize"
          :height="starSize"
          :fill-percentage="getStarFillPercentage(n)"
          :active-color="activeColor"
          :inactive-color="inactiveColor"
        />
      </div>
    </div>
    <div
      v-else
      role="radiogroup"
      :aria-labelledby="ariaLabelledby || null"
      :aria-required="required"
      :aria-invalid="ariaInvalid"
      :aria-describedby="ariaDescribedby || null"
      class="ra-star-rating__stars-wrapper"
    >
      <div
        v-for="n in maxRating"
        :ref="`star${n}`"
        :key="n"
        role="radio"
        :aria-checked="n === rating ? 'true' : 'false'"
        tabindex="0"
        class="ra-star-rating__star"
        :aria-label="getAriaLabel(n)"
        @mouseover="hoveredRating = n"
        @mouseleave="hoveredRating = null"
        @blur="forceFocusOutline = false"
        @keydown.space.prevent="selectRating(n)"
        @keydown.enter="selectRating(n)"
        @click="selectRating(n)"
      >
        <refapp-star-icon
          :width="starSize"
          :height="starSize"
          :fill-percentage="getStarFillPercentage(n)"
          :active-color="activeColor"
          :inactive-color="inactiveColor"
        />
      </div>
    </div>
  </div>
</template>

<script>
import RefappStarIcon from './refapp-star-icon.vue';

export default {
  name: 'refapp-star-rating',
  components: {
    RefappStarIcon,
  },
  props: {
    /**
     * Controls how precise the rating will be rendered.
     * Component rounds the value based on this setting.
     *
     * Expects a number between 0.01 - 1.
     *
     * 0.5 for half stars, 0.01 for fluid stars.
     */
    increment: {
      type: Number,
      default: 1,
    },
    rating: {
      type: Number,
      default: 0,
    },
    maxRating: {
      type: Number,
      default: 5,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    starSize: {
      type: Number,
      default: 28, // px
    },
    activeColor: {
      type: String,
      default: '#ffd055',
    },
    inactiveColor: {
      type: String,
      default: '#d8d8d8',
    },
    /**
     * Label for radiogroup.
     * Required for editable mode - readonly="false"
     */
    ariaLabelledby: {
      type: String,
      default: '',
      // props as 2nd arg work from vue 3.4
      validator(value, props) {
        if (!props || props.readonly) return true;
        return !!value;
      },
    },
    /**
     * Label for readonly mode or for individual stars in editable mode.
     */
    ariaLabel: {
      type: String,
      required: true,
      default: '{{star}} out of {{maxStars}} stars',
      validator(value) {
        return value.includes('{{star}}') && value.includes('{{maxStars}}');
      },
    },
    ariaInvalid: {
      type: Boolean,
      default: false,
    },
    ariaDescribedby: {
      type: String,
      default: '',
    },
    starClasses: {
      type: String,
      default: '',
    },
  },
  emits: ['update:rating'],
  expose: ['focus'],
  data() {
    return {
      hoveredRating: 0,
      forceFocusOutline: false,
    };
  },
  computed: {
    roundedRating() {
      const inv = 1 / this.increment;
      return Math.min(this.maxRating, Math.ceil(this.rating * inv) / inv);
    },
    ratingWholeNumber() {
      return Math.floor(this.roundedRating);
    },
    ratingRemainderPercentage() {
      return Number((this.roundedRating % 1).toFixed(2)) * 100;
    },
  },
  methods: {
    focus() {
      const firstStar = this.$refs.star1[0];
      if (firstStar) {
        this.forceFocusOutline = true;
        firstStar.focus();
      }
    },
    getStarFillPercentage(starPosition) {
      // Hover state takes priority when editable
      if (!this.readonly && this.hoveredRating) {
        return starPosition <= this.hoveredRating ? 100 : 0;
      }

      // Full star
      if (starPosition <= this.ratingWholeNumber) {
        return 100;
      }

      // Partial star
      if (this.ratingRemainderPercentage && starPosition === this.ratingWholeNumber + 1) {
        return this.ratingRemainderPercentage;
      }

      // Empty star
      return 0;
    },
    selectRating(selectedRating) {
      if (!this.readonly) {
        this.$emit('update:rating', selectedRating === this.rating ? 0 : selectedRating);
        this.hoveredRating = null;
      }
    },
    getAriaLabel(rating) {
      return (
        this.ariaLabel.replace('{{star}}', rating).replace('{{maxStars}}', this.maxRating) || null
      );
    },
  },
};
</script>

<style lang="scss">
.ra-star-rating {
  $root: &;

  display: inline-block;

  &__stars-wrapper {
    display: flex;
  }

  &--editable {
    #{$root}__star {
      cursor: pointer;
    }
  }

  &--force-focus-outline {
    #{$root}__star:focus {
      outline: 2px auto #000;
    }
  }
}
</style>
